/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
/**
 * Component that creates a 3D panel
 */

import React, { useEffect, useRef } from "react";
import BackgroundPanel from "./BackgroundPanel";
import SelectionButton from "./SelectionButton";
import "../../aframe_components/AutoResize";
import "../../aframe_components/CenterObject";

function Panel({
    width,
    height,
    depth = 0,
    children,
    backgroundColor,
    opacity,
    xPadding,
    yPadding,
    panel = false,
    resize = false,
    title = false,
    fontColor,
    visible = false,
    resizePrecision,
    loading,
    textSizeDecimal,
    selection,
    currentSelection,
    setSelectionChoices,
    selectionChoices,
    enableReload,
    setLoading,
    aframeClasses,
    notRepositionate = false,
    ...otherAttributes
}) {
    useEffect(() => {
        if (!resize || !children) return;
        const centerElem = autoCenterRef.current;
        const resizeElem = autoResizeRef.current;
        if (
            !enableReload &&
            (centerElem.hasAttribute("panelfinished") ||
                resizeElem.hasAttribute("panelfinished") ||
                centerElem.hasAttribute("centerobject") ||
                resizeElem.hasAttribute("panelautoresize"))
        )
            return;
        if (loading) {
            setLoading((old) => (old ? old + 2 : 2));
            resizeElem.addEventListener("isLoaded", loadingHandler);
        }
        centerElem.setAttribute("centerobject", {
            precision: resizePrecision,
            alert: loading
        });
        resizeElem.setAttribute("panelautoresize", {
            width: width - 2 * xPadding,
            height: height - (title ? 4 : 2) * yPadding,
            depth,
            precision: resizePrecision,
            alert: loading
        });

        return () => {
            if (loading) {
                resizeElem.removeEventListener("isLoaded", loadingHandler);
                setLoading((old) => 0);
            }
        };
    });

    const autoResizeRef = useRef();
    const autoCenterRef = useRef();

    if (!children) return null;

    function loadingHandler(e) {
        e.stopPropagation();
        setLoading((old) => old - 1);
    }

    if (!xPadding) xPadding = width / 20;
    if (!yPadding) yPadding = height / 20;

    const selectionElem = selection ? (
        <SelectionButton
            id={selectionChoices[0] + width}
            aframeClasses={aframeClasses}
            width={width / 4}
            height={height / 15}
            current={currentSelection}
            choices={selectionChoices}
            setCurrent={setSelectionChoices}
            position={`${width / 2 - width / 8 - xPadding} ${
                height / 2 - yPadding - height / 40
            } ${width / 100}`}
            textSizeDecimal={
                textSizeDecimal ? textSizeDecimal * 1.5 : 15 / (width / 5)
            }
        />
    ) : null;

    const entity = (
        <a-entity position={`0 ${title ? -yPadding : 0} 0.001`}>
            <a-entity
                ref={autoResizeRef}
                class="PanelAutoResize"
                visible={!resize || visible}
            >
                <a-entity
                    ref={autoCenterRef}
                    class="ObjectRepositioning"
                    visible={!resize || visible}
                >
                    {children}
                </a-entity>
            </a-entity>
        </a-entity>
    );

    return panel ? (
        <BackgroundPanel
            width={width}
            height={height}
            opacity={opacity}
            backgroundColor={backgroundColor}
            title={title}
            textSizeDecimal={textSizeDecimal}
            textOffset={{ x: xPadding, y: yPadding }}
            {...otherAttributes}
        >
            {selection && selectionElem}
            {entity}
        </BackgroundPanel>
    ) : (
        <a-entity {...otherAttributes}>
            {selection && selectionElem}
            {entity}
        </a-entity>
    );
}

export default Panel;
